import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const ProposalSentModal = ({
  isOpen,
  onClose,
  onSuccess,
  gym
}) => {
  const [step, setStep] = useState('preview'); // preview, sending, success, error
  const [error, setError] = useState(null);
  const [emailResult, setEmailResult] = useState(null);

  // Form data for proposal
  const [formData, setFormData] = useState({
    proposalAmount: '',
    ownerEmail: '',
    remarks: ''
  });

  // Initialize form with gym data
  useEffect(() => {
    if (isOpen && gym) {
      setFormData({
        proposalAmount: gym.proposalAmount?.toString() || '',
        ownerEmail: gym.ownerEmail || '',
        remarks: gym.remarks || ''
      });
      setStep('preview');
      setError(null);
      setEmailResult(null);
    }
  }, [isOpen, gym]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const validateForm = () => {
    if (!formData.ownerEmail?.trim()) {
      setError('Owner email is required to send the proposal');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSendProposal = async () => {
    if (!validateForm()) return;

    try {
      setStep('sending');
      setError(null);

      // First update the gym with proposal details
      await axios.put(`/api/gyms/${gym.id}`, {
        proposalAmount: formData.proposalAmount ? parseFloat(formData.proposalAmount) : null,
        ownerEmail: formData.ownerEmail,
        remarks: formData.remarks
      });

      // Then move the gym to Proposal Sent stage (this triggers the email)
      const response = await axios.put(`/api/gyms/${gym.id}/stage`, {
        stage: 'Proposal Sent',
        comments: `Proposal sent to ${formData.ownerEmail}`
      });

      setEmailResult({
        success: response.data.emailSent,
        error: response.data.emailError,
        gym: response.data.gym
      });

      if (response.data.emailSent) {
        setStep('success');
      } else {
        setStep('warning');
      }

    } catch (error) {
      console.error('Error sending proposal:', error);
      setError(error.response?.data?.error || 'Failed to send proposal');
      setStep('error');
    }
  };

  const handleClose = () => {
    if (step === 'sending') return; // Don't close while sending

    if (step === 'success' || step === 'warning') {
      // If successful, notify parent
      onSuccess({ gym: emailResult?.gym });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="text-white">
            <h2 className="text-xl font-semibold">Send Proposal</h2>
            <p className="text-sm text-blue-100 mt-1">
              {gym?.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-blue-200"
            disabled={step === 'sending'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Preview Step */}
          {step === 'preview' && (
            <div className="space-y-6">
              {/* Email Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-blue-800">Email Notification</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      A partnership proposal email will be sent to the gym owner with all the details below.
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Owner Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={handleInputChange}
                  placeholder="owner@example.com"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The proposal will be sent to this email address
                </p>
              </div>

              {/* Proposal Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proposal Amount (INR)
                </label>
                <input
                  type="number"
                  name="proposalAmount"
                  value={formData.proposalAmount}
                  onChange={handleInputChange}
                  placeholder="e.g., 500000"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  placeholder="Any additional notes about the proposal..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email Preview */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700">Email Preview</h4>
                </div>
                <div className="p-4 bg-white">
                  <div className="space-y-2 text-sm">
                    <p><strong>To:</strong> {formData.ownerEmail || 'Not specified'}</p>
                    <p><strong>Subject:</strong> Partnership Proposal from WTF Gyms - {gym?.name}</p>
                    <hr className="my-3" />
                    <p><strong>Gym:</strong> {gym?.name}</p>
                    <p><strong>Location:</strong> {gym?.location || gym?.address || 'Not specified'}</p>
                    <p><strong>Owner:</strong> {gym?.contact_person || 'Not specified'}</p>
                    {formData.proposalAmount && (
                      <p><strong>Proposal Amount:</strong> {formatCurrency(formData.proposalAmount)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sending Step */}
          {step === 'sending' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mb-6"></div>
              <h3 className="text-lg font-semibold text-gray-800">Sending Proposal...</h3>
              <p className="text-sm text-gray-500 mt-2">Please wait while we send the email to {formData.ownerEmail}</p>

              {/* Progress Steps */}
              <div className="mt-8 space-y-3 w-full max-w-sm">
                <div className="flex items-center text-sm">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-600">Updating gym details</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 animate-pulse">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-gray-600">Sending email...</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  </div>
                  <span className="text-gray-400">Moving to Proposal Sent</span>
                </div>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Proposal Sent Successfully!</h3>
              <p className="text-sm text-gray-500 mt-2 text-center">
                The partnership proposal has been sent to<br />
                <strong className="text-gray-700">{formData.ownerEmail}</strong>
              </p>

              {/* Success Details */}
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 w-full max-w-sm">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gym:</span>
                    <span className="font-medium">{gym?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{formData.ownerEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-green-600">Delivered</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Warning Step - Email failed but stage moved */}
          {step === 'warning' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Stage Updated</h3>
              <p className="text-sm text-gray-500 mt-2 text-center">
                The gym has been moved to "Proposal Sent" but the email could not be sent.
              </p>

              {/* Warning Details */}
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 w-full max-w-sm">
                <p className="text-sm text-yellow-800">
                  <strong>Reason:</strong> {emailResult?.error || 'Email service unavailable'}
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  Please send the proposal manually to: {formData.ownerEmail}
                </p>
              </div>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Failed to Send Proposal</h3>
              <p className="text-sm text-red-600 mt-2 text-center">{error}</p>

              <button
                onClick={() => setStep('preview')}
                className="mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          {step === 'preview' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendProposal}
                disabled={!formData.ownerEmail}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Proposal
              </button>
            </>
          )}

          {(step === 'success' || step === 'warning') && (
            <button
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              Done
            </button>
          )}

          {step === 'error' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalSentModal;
